import { create } from 'zustand';
import { toast } from 'sonner';
import {
    billingService,
    Invoice,
    Payment,
    Receipt,
    InvoiceStats,
    BillingSettings,
    CreateInvoiceDto,
    UpdateInvoiceDto,
    CreatePaymentDto,
    InvoiceFilters,
} from '../services/billing.service';

interface BillingState {
    // Data
    invoices: Invoice[];
    payments: Payment[];
    receipts: Receipt[];
    selectedInvoice: Invoice | null;
    stats: InvoiceStats | null;
    settings: BillingSettings | null;

    // UI State
    isLoading: boolean;
    activeTab: 'invoices' | 'payments' | 'receipts' | 'settings' | 'reports';
    filters: InvoiceFilters;
    searchTerm: string;
    totalInvoices: number;

    // Invoice Actions
    fetchInvoices: (filters?: InvoiceFilters) => Promise<void>;
    fetchInvoice: (id: string) => Promise<Invoice>;
    fetchStats: () => Promise<void>;
    createInvoice: (data: CreateInvoiceDto) => Promise<Invoice>;
    updateInvoice: (id: string, data: UpdateInvoiceDto) => Promise<Invoice>;
    deleteInvoice: (id: string) => Promise<void>;
    sendInvoice: (id: string) => Promise<void>;
    sendReminder: (id: string) => Promise<void>;

    // Payment Actions
    fetchPayments: (invoiceId?: string) => Promise<void>;
    recordPayment: (data: CreatePaymentDto) => Promise<Payment>;
    updatePayment: (id: string, data: Partial<Payment>) => Promise<void>;

    // Receipt Actions
    fetchReceipts: (invoiceId?: string) => Promise<void>;
    fetchReceipt: (id: string) => Promise<Receipt>;
    sendReceipt: (id: string) => Promise<void>;

    // Settings Actions
    fetchSettings: () => Promise<void>;
    updateSettings: (data: Partial<BillingSettings>) => Promise<void>;

    // UI Actions
    setActiveTab: (tab: BillingState['activeTab']) => void;
    setFilters: (filters: InvoiceFilters) => void;
    setSearchTerm: (term: string) => void;
    setSelectedInvoice: (invoice: Invoice | null) => void;

    // Helpers
    getFilteredInvoices: () => Invoice[];
}

export const useBillingStore = create<BillingState>((set, get) => ({
    // Initial State
    invoices: [],
    payments: [],
    receipts: [],
    selectedInvoice: null,
    stats: null,
    settings: null,
    isLoading: false,
    activeTab: 'invoices',
    filters: {},
    searchTerm: '',
    totalInvoices: 0,

    // ============ Invoice Actions ============

    fetchInvoices: async (filters?: InvoiceFilters) => {
        set({ isLoading: true });
        try {
            const appliedFilters = filters || get().filters;
            const { invoices, total } = await billingService.getInvoices(appliedFilters);
            set({ invoices, totalInvoices: total, isLoading: false });
        } catch (error) {
            console.error('Failed to fetch invoices:', error);
            toast.error('Failed to load invoices');
            set({ isLoading: false });
        }
    },

    fetchInvoice: async (id: string) => {
        try {
            const invoice = await billingService.getInvoice(id);
            set({ selectedInvoice: invoice });
            return invoice;
        } catch (error) {
            console.error('Failed to fetch invoice:', error);
            toast.error('Failed to load invoice');
            throw error;
        }
    },

    fetchStats: async () => {
        try {
            const stats = await billingService.getInvoiceStats();
            set({ stats });
        } catch (error) {
            console.error('Failed to fetch invoice stats:', error);
        }
    },

    createInvoice: async (data: CreateInvoiceDto) => {
        try {
            const invoice = await billingService.createInvoice(data);
            set((state) => ({ invoices: [invoice, ...state.invoices] }));
            toast.success('Invoice created successfully');
            get().fetchStats();
            return invoice;
        } catch (error) {
            console.error('Failed to create invoice:', error);
            toast.error('Failed to create invoice');
            throw error;
        }
    },

    updateInvoice: async (id: string, data: UpdateInvoiceDto) => {
        try {
            const updated = await billingService.updateInvoice(id, data);
            set((state) => ({
                invoices: state.invoices.map((inv) => (inv.id === id ? updated : inv)),
                selectedInvoice: state.selectedInvoice?.id === id ? updated : state.selectedInvoice,
            }));
            toast.success('Invoice updated successfully');
            return updated;
        } catch (error) {
            console.error('Failed to update invoice:', error);
            toast.error('Failed to update invoice');
            throw error;
        }
    },

    deleteInvoice: async (id: string) => {
        try {
            await billingService.deleteInvoice(id);
            set((state) => ({
                invoices: state.invoices.filter((inv) => inv.id !== id),
                selectedInvoice: state.selectedInvoice?.id === id ? null : state.selectedInvoice,
            }));
            toast.success('Invoice deleted');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to delete invoice:', error);
            toast.error('Failed to delete invoice');
            throw error;
        }
    },

    sendInvoice: async (id: string) => {
        try {
            const updated = await billingService.sendInvoice(id);
            set((state) => ({
                invoices: state.invoices.map((inv) => (inv.id === id ? updated : inv)),
                selectedInvoice: state.selectedInvoice?.id === id ? updated : state.selectedInvoice,
            }));
            toast.success('Invoice sent successfully');
            get().fetchStats();
        } catch (error) {
            console.error('Failed to send invoice:', error);
            toast.error('Failed to send invoice');
        }
    },

    sendReminder: async (id: string) => {
        try {
            const updated = await billingService.sendReminder(id);
            set((state) => ({
                invoices: state.invoices.map((inv) => (inv.id === id ? updated : inv)),
                selectedInvoice: state.selectedInvoice?.id === id ? updated : state.selectedInvoice,
            }));
            toast.success('Reminder sent');
        } catch (error) {
            console.error('Failed to send reminder:', error);
            toast.error('Failed to send reminder');
        }
    },

    // ============ Payment Actions ============

    fetchPayments: async (invoiceId?: string) => {
        try {
            const payments = await billingService.getPayments(invoiceId);
            set({ payments });
        } catch (error) {
            console.error('Failed to fetch payments:', error);
            toast.error('Failed to load payments');
        }
    },

    recordPayment: async (data: CreatePaymentDto) => {
        try {
            const payment = await billingService.recordPayment(data);
            set((state) => ({ payments: [payment, ...state.payments] }));
            toast.success('Payment recorded successfully');
            get().fetchInvoices();
            get().fetchStats();
            return payment;
        } catch (error) {
            console.error('Failed to record payment:', error);
            toast.error('Failed to record payment');
            throw error;
        }
    },

    updatePayment: async (id: string, data: Partial<Payment>) => {
        try {
            const updated = await billingService.updatePayment(id, data);
            set((state) => ({
                payments: state.payments.map((p) => (p.id === id ? updated : p)),
            }));
            toast.success('Payment updated');
        } catch (error) {
            console.error('Failed to update payment:', error);
            toast.error('Failed to update payment');
        }
    },

    // ============ Receipt Actions ============

    fetchReceipts: async (invoiceId?: string) => {
        try {
            const receipts = await billingService.getReceipts(invoiceId);
            set({ receipts });
        } catch (error) {
            console.error('Failed to fetch receipts:', error);
            toast.error('Failed to load receipts');
        }
    },

    fetchReceipt: async (id: string) => {
        try {
            const receipt = await billingService.getReceipt(id);
            return receipt;
        } catch (error) {
            console.error('Failed to fetch receipt:', error);
            toast.error('Failed to load receipt');
            throw error;
        }
    },

    sendReceipt: async (id: string) => {
        try {
            await billingService.sendReceipt(id);
            toast.success('Receipt sent');
        } catch (error) {
            console.error('Failed to send receipt:', error);
            toast.error('Failed to send receipt');
        }
    },

    // ============ Settings Actions ============

    fetchSettings: async () => {
        try {
            const settings = await billingService.getSettings();
            set({ settings });
        } catch (error) {
            console.error('Failed to fetch billing settings:', error);
        }
    },

    updateSettings: async (data: Partial<BillingSettings>) => {
        try {
            const updated = await billingService.updateSettings(data);
            set({ settings: updated });
            toast.success('Billing settings updated');
        } catch (error) {
            console.error('Failed to update settings:', error);
            toast.error('Failed to update settings');
        }
    },

    // ============ UI Actions ============

    setActiveTab: (tab) => set({ activeTab: tab }),
    setFilters: (filters) => {
        set({ filters });
        get().fetchInvoices(filters);
    },
    setSearchTerm: (term) => set({ searchTerm: term }),
    setSelectedInvoice: (invoice) => set({ selectedInvoice: invoice }),

    // ============ Helpers ============

    getFilteredInvoices: () => {
        const { invoices, activeTab, searchTerm } = get();

        return invoices.filter((invoice) => {
            // Tab filter
            if (activeTab === 'invoices') {
                // Use status filter from filters if set
                const statusFilter = get().filters.status;
                if (statusFilter && invoice.status !== statusFilter) return false;
            }

            // Search filter
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return (
                    invoice.invoiceNumber.toLowerCase().includes(term) ||
                    invoice.billTo.companyName.toLowerCase().includes(term) ||
                    invoice.billTo.contactName?.toLowerCase().includes(term) ||
                    invoice.billTo.email.toLowerCase().includes(term)
                );
            }

            return true;
        });
    },
}));
